import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import stepImageOne from "../assets/img/gamejam-help/webgl-help-2.png";
import stepImageTwo from "../assets/img/gamejam-help/webgl-help-3.png";
import stepImageThree from "../assets/img/gamejam-help/webgl-help-4.png";
import webglGuidePdf from "../assets/webgl_guide.pdf";

const steps = [
  {
    id: "01",
    title: "Install WebGL Build Support",
    body:
      "Open Unity Hub, go to Installs, choose your Unity version, click the manage gear icon, and add the WebGL Build Support module if it is not installed yet.",
  },
  {
    id: "02",
    title: "Configure Build Settings",
    body:
      "Inside Unity, open File > Build Settings or Build Profiles, select Web as the platform, switch to it, and make sure the required scenes are included in the build.",
  },
  {
    id: "03",
    title: "Adjust Player Settings",
    body:
      "Open Player Settings and review resolution, template, compression, and decompression fallback. Brotli is great for smaller builds, while Gzip offers wider compatibility.",
  },
  {
    id: "04",
    title: "Build the Project",
    body:
      'Click Build and export into a separate folder outside Assets. Unity should generate an `index.html` file along with `Build` and `TemplateData` folders.',
  },
  {
    id: "05",
    title: "Run and Test Correctly",
    body:
      "Do not test by double-clicking the local HTML file in most browsers. Use Build and Run, VS Code Live Server, or a simple local server so the WebGL files load properly.",
  },
];

const quickTips = [
  "In Build settings include all scenes that are needed for the game.",
  "Enable Decompression Fallback if your hosting server is not configured yet.",
  "Test the exported build in a browser before uploading to CGES.",
  "Build into a clean folder each time so old files do not mix with the new export.",
];

const screenshots = [
  {
    src: stepImageOne,
    alt: "Unity Build Profiles window with Web platform selected",
    title: "Select Web and confirm your scene list",
  },
  {
    src: stepImageTwo,
    alt: "Unity Player Settings showing Web publishing settings",
    title: "Review compression and publishing settings",
  },
  {
    src: stepImageThree,
    alt: "Unity Web player settings close-up for a WebGL build",
    title: "Fine-tune memory and compatibility options",
  },
];

export default function GameJamWebGLHelp() {
  const [activeShot, setActiveShot] = useState(null);

  useEffect(() => {
    if (!activeShot) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveShot(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeShot]);

  return (
    <>
      <style>
        {`
          .webgl-help-page {
            --bg: #080808;
            --panel: #121212;
            --panel-2: #1a1a1a;
            --text: #ffffff;
            --muted: rgba(255,255,255,.72);
            --soft: rgba(255,255,255,.45);
            --accent: #f97316;
            --accent-2: #fb923c;
            --border: rgba(249,115,22,.18);
            --border-strong: rgba(249,115,22,.35);
            color: var(--text);
            background:
              radial-gradient(circle at top left, rgba(249,115,22,.18), transparent 30%),
              linear-gradient(180deg, #0d0d0d 0%, #080808 100%);
          }
          .webgl-help-page * { box-sizing: border-box; }
          .webgl-help-page a { color: inherit; }
          .webgl-help-shell {
            width: min(1180px, calc(100% - 48px));
            margin: 0 auto;
          }
          .webgl-help-hero {
            padding: 88px 0 54px;
          }
          .webgl-help-kicker {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            font: 700 12px 'Barlow Condensed', sans-serif;
            letter-spacing: 3px;
            text-transform: uppercase;
            color: var(--accent);
            margin-bottom: 18px;
          }
          .webgl-help-kicker::before {
            content: '';
            width: 28px;
            height: 2px;
            background: currentColor;
          }
          .webgl-help-hero-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.15fr) minmax(300px, .85fr);
            gap: 26px;
            align-items: stretch;
          }
          .webgl-help-title {
            font: 900 clamp(38px, 6vw, 72px) / .96 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
            margin: 0 0 18px;
          }
          .webgl-help-title span {
            color: var(--accent);
          }
          .webgl-help-copy {
            font: 400 17px / 1.75 'Barlow', sans-serif;
            color: var(--muted);
            max-width: 760px;
            margin: 0 0 28px;
          }
          .webgl-help-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 14px;
          }
          .webgl-help-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 50px;
            padding: 0 22px;
            text-decoration: none;
            font: 800 13px 'Barlow Condensed', sans-serif;
            letter-spacing: 2px;
            text-transform: uppercase;
            clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px));
            transition: transform .2s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease;
          }
          .webgl-help-btn:hover { transform: translateY(-2px); }
          .webgl-help-btn.primary {
            background: linear-gradient(135deg, var(--accent), var(--accent-2));
            color: #fff;
            box-shadow: 0 16px 38px rgba(249,115,22,.22);
          }
          .webgl-help-btn.secondary {
            border: 1px solid rgba(255,255,255,.16);
            background: rgba(255,255,255,.04);
          }
          .webgl-help-summary {
            background: linear-gradient(180deg, rgba(249,115,22,.12), rgba(255,255,255,.03));
            border: 1px solid var(--border-strong);
            padding: 26px;
            clip-path: polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%);
          }
          .webgl-help-summary h3 {
            font: 800 14px 'Barlow Condensed', sans-serif;
            letter-spacing: 3px;
            text-transform: uppercase;
            color: var(--accent);
            margin: 0 0 16px;
          }
          .webgl-help-summary ul {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            gap: 12px;
          }
          .webgl-help-summary li {
            position: relative;
            padding-left: 18px;
            color: var(--muted);
            line-height: 1.65;
          }
          .webgl-help-summary li::before {
            content: '';
            position: absolute;
            left: 0;
            top: 11px;
            width: 7px;
            height: 7px;
            background: var(--accent);
            transform: rotate(45deg);
          }
          .webgl-help-section {
            padding: 18px 0 64px;
          }
          .webgl-help-section-head {
            margin-bottom: 24px;
          }
          .webgl-help-section-head h2 {
            font: 900 clamp(28px, 4vw, 46px) / 1 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
            margin: 0 0 10px;
          }
          .webgl-help-section-head p {
            margin: 0;
            color: var(--muted);
            line-height: 1.7;
          }
          .webgl-help-steps {
            display: grid;
            gap: 18px;
          }
          .webgl-help-step {
            display: grid;
            grid-template-columns: 88px minmax(0, 1fr);
            gap: 20px;
            background: linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.02));
            border: 1px solid rgba(255,255,255,.08);
            padding: 24px;
          }
          .webgl-help-step-no {
            font: 900 34px 'Barlow Condensed', sans-serif;
            color: var(--accent);
            line-height: 1;
          }
          .webgl-help-step h3 {
            font: 800 22px 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
            margin: 0 0 8px;
          }
          .webgl-help-step p {
            margin: 0;
            color: var(--muted);
            line-height: 1.72;
          }
          .webgl-help-gallery {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 18px;
          }
          .webgl-help-download {
            margin-top: 18px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 18px;
            background: linear-gradient(180deg, rgba(249,115,22,.08), rgba(255,255,255,.02));
            border: 1px solid var(--border-strong);
            padding: 22px 24px;
          }
          .webgl-help-download-copy h3 {
            margin: 0 0 8px;
            font: 800 22px 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
          }
          .webgl-help-download-copy p {
            margin: 0;
            color: var(--muted);
            line-height: 1.7;
          }
          .webgl-help-shot {
            background: var(--panel);
            border: 1px solid var(--border);
            overflow: hidden;
          }
          .webgl-help-shot-btn {
            display: block;
            width: 100%;
            padding: 0;
            border: 0;
            background: transparent;
            cursor: zoom-in;
            text-align: left;
          }
          .webgl-help-shot img {
            display: block;
            width: 100%;
            height: auto;
          }
          .webgl-help-shot-body {
            padding: 16px 18px 18px;
          }
          .webgl-help-shot-body h3 {
            font: 800 18px 'Barlow Condensed', sans-serif;
            margin: 0 0 8px;
            text-transform: uppercase;
          }
          .webgl-help-shot-body p {
            margin: 0;
            color: var(--soft);
            line-height: 1.65;
          }
          .webgl-help-shot-hint {
            display: inline-flex;
            align-items: center;
            margin-top: 12px;
            font: 700 11px 'Barlow Condensed', sans-serif;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: var(--accent);
          }
          .webgl-help-tips {
            display: grid;
            grid-template-columns: 1.1fr .9fr;
            gap: 18px;
          }
          .webgl-help-panel {
            background: var(--panel-2);
            border: 1px solid rgba(255,255,255,.06);
            padding: 24px;
          }
          .webgl-help-panel h3 {
            font: 800 20px 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
            margin: 0 0 14px;
          }
          .webgl-help-panel p {
            margin: 0;
            color: var(--muted);
            line-height: 1.72;
          }
          .webgl-help-panel ul {
            list-style: none;
            margin: 0;
            padding: 0;
            display: grid;
            gap: 12px;
          }
          .webgl-help-panel li {
            padding: 14px 16px;
            border: 1px solid rgba(249,115,22,.15);
            background: rgba(249,115,22,.05);
            color: var(--muted);
            line-height: 1.6;
          }
          .webgl-help-lightbox {
            position: fixed;
            inset: 0;
            z-index: 2000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 28px;
            background: rgba(0,0,0,.9);
          }
          .webgl-help-lightbox-backdrop {
            position: absolute;
            inset: 0;
            border: 0;
            background: transparent;
            cursor: zoom-out;
          }
          .webgl-help-lightbox-inner {
            position: relative;
            z-index: 1;
            width: min(1400px, 100%);
            max-height: 100%;
            display: grid;
            gap: 14px;
          }
          .webgl-help-lightbox-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          }
          .webgl-help-lightbox-meta h3 {
            margin: 0 0 4px;
            font: 800 22px 'Barlow Condensed', sans-serif;
            text-transform: uppercase;
          }
          .webgl-help-lightbox-meta p {
            margin: 0;
            color: var(--muted);
          }
          .webgl-help-lightbox-close {
            border: 1px solid rgba(255,255,255,.14);
            background: rgba(255,255,255,.06);
            color: #fff;
            min-width: 46px;
            height: 46px;
            font-size: 24px;
            line-height: 1;
            cursor: pointer;
          }
          .webgl-help-lightbox-frame {
            overflow: auto;
            max-height: calc(100vh - 140px);
            background: rgba(255,255,255,.03);
            border: 1px solid rgba(255,255,255,.08);
          }
          .webgl-help-lightbox-frame img {
            display: block;
            width: 100%;
            height: auto;
          }
          @media (max-width: 900px) {
            .webgl-help-hero-grid,
            .webgl-help-tips,
            .webgl-help-gallery {
              grid-template-columns: 1fr;
            }
            .webgl-help-download {
              align-items: flex-start;
              flex-direction: column;
            }
          }
          @media (max-width: 640px) {
            .webgl-help-shell {
              width: min(100% - 32px, 1180px);
            }
            .webgl-help-hero {
              padding-top: 70px;
            }
            .webgl-help-step {
              grid-template-columns: 1fr;
              gap: 12px;
              padding: 20px;
            }
            .webgl-help-lightbox {
              padding: 16px;
            }
            .webgl-help-lightbox-top {
              align-items: flex-start;
            }
            .webgl-help-lightbox-meta h3 {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="webgl-help-page">
        <section className="webgl-help-hero">
          <div className="webgl-help-shell">
            <div className="webgl-help-kicker">Need Help Resources</div>
            <div className="webgl-help-hero-grid">
              <div>
                <h1 className="webgl-help-title">
                  How To Take A <span>WebGL Build</span>
                </h1>
                <p className="webgl-help-copy">
                  This page turns the shared walkthrough into a clean step-by-step guide for
                  participants who need help exporting their Unity game for the CGES Game Jam.
                  Follow the setup, build, and testing flow below before you submit.
                </p>
                <div className="webgl-help-actions">
                  <a
                    className="webgl-help-btn primary"
                    href="https://www.youtube.com/watch?v=gHZhZ9boG5A"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Watch Reference Video
                  </a>
                  <Link className="webgl-help-btn secondary" to="/gamejam">
                    Back To Game Jam
                  </Link>
                </div>
              </div>

              <aside className="webgl-help-summary">
                <h3>Quick Checklist</h3>
                <ul>
                  <li>Add the WebGL module from Unity Hub before you start.</li>
                  <li>Switch your project to the Web platform in Build Profiles.</li>
                  <li>Review publishing settings before building the final export.</li>
                  <li>Test on a local server instead of opening the HTML file directly.</li>
                </ul>
              </aside>
            </div>
          </div>
        </section>

        <section className="webgl-help-section">
          <div className="webgl-help-shell">
            <div className="webgl-help-section-head">
              <h2>Step By Step</h2>
              <p>
                These are the exact stages pulled from the provided guide, simplified for
                fast use during the jam.
              </p>
            </div>

            <div className="webgl-help-steps">
              {steps.map((step) => (
                <article className="webgl-help-step" key={step.id}>
                  <div className="webgl-help-step-no">{step.id}</div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="webgl-help-download">
              <div className="webgl-help-download-copy">
                <h3>Download Instruction Document</h3>
                <p>
                  Need the full reference guide offline? Download the PDF version of the
                  WebGL instructions directly here.
                </p>
              </div>
              <a className="webgl-help-btn primary" href={webglGuidePdf} download>
                Download WebGL Guide
              </a>
            </div>
          </div>
        </section>

        <section className="webgl-help-section">
          <div className="webgl-help-shell">
            <div className="webgl-help-section-head">
              <h2>Guide Screens</h2>
              <p>
                These screenshots from the shared document show where the key Web build
                settings live inside Unity.
              </p>
            </div>

            <div className="webgl-help-gallery">
              {screenshots.map((shot) => (
                <article className="webgl-help-shot" key={shot.title}>
                  <button
                    type="button"
                    className="webgl-help-shot-btn"
                    onClick={() => setActiveShot(shot)}
                    aria-label={`Open fullscreen preview for ${shot.title}`}
                  >
                    <img src={shot.src} alt={shot.alt} />
                  </button>
                  <div className="webgl-help-shot-body">
                    <h3>{shot.title}</h3>
                    <p>{shot.alt}</p>
                    <span className="webgl-help-shot-hint">Click for full screen</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="webgl-help-section">
          <div className="webgl-help-shell">
            <div className="webgl-help-tips">
              <div className="webgl-help-panel">
                <h3>Before You Submit</h3>
                <ul>
                  {quickTips.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
              <div className="webgl-help-panel">
                <h3>Common Gotcha</h3>
                <p>
                  If your exported build does not open when you double-click `index.html`,
                  that usually does not mean the build is broken. WebGL builds often need a
                  local or hosted web server to load their data files correctly. Unity Build
                  and Run is the easiest option while testing.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {activeShot ? (
        <div className="webgl-help-lightbox" role="dialog" aria-modal="true" aria-label={activeShot.title}>
          <button
            type="button"
            className="webgl-help-lightbox-backdrop"
            aria-label="Close fullscreen image preview"
            onClick={() => setActiveShot(null)}
          />
          <div className="webgl-help-lightbox-inner">
            <div className="webgl-help-lightbox-top">
              <div className="webgl-help-lightbox-meta">
                <h3>{activeShot.title}</h3>
                <p>{activeShot.alt}</p>
              </div>
              <button
                type="button"
                className="webgl-help-lightbox-close"
                onClick={() => setActiveShot(null)}
                aria-label="Close fullscreen image preview"
              >
                ×
              </button>
            </div>
            <div className="webgl-help-lightbox-frame">
              <img src={activeShot.src} alt={activeShot.alt} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
